export type Listener<T> = (payload: T) => void;
export type Unsubscribe = () => void;

export class EventBus<TMap> {
  private readonly listeners = new Map<keyof TMap, Set<Listener<unknown>>>();

  on<K extends keyof TMap>(event: K, listener: Listener<TMap[K]>): Unsubscribe {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(listener as Listener<unknown>);
    return () => this.off(event, listener);
  }

  off<K extends keyof TMap>(event: K, listener: Listener<TMap[K]>): void {
    this.listeners.get(event)?.delete(listener as Listener<unknown>);
  }

  emit<K extends keyof TMap>(event: K, payload: TMap[K]): void {
    const set = this.listeners.get(event);
    if (!set || set.size === 0) return;
    // Snapshot so unsubscribe-during-emit is safe.
    for (const listener of Array.from(set)) {
      (listener as Listener<TMap[K]>)(payload);
    }
  }

  clear(): void {
    this.listeners.clear();
  }
}
